/**
 * DogDetail (#553) — the dedicated Pet Profile page (`/dogs/:dogId`).
 *
 * The all-inclusive pet record, redesigned to the Design Philosophy: a sticky
 * identity header, stacked section cards (one raised elevation), definition
 * lists that render a fact ONLY when it's known (honesty #546), and a loud
 * safety band for allergies / banned / special-needs. Every field the schema
 * stores is displayed or honestly empty-stated; Gingr fields with no column
 * are deferred (see the PR body), never fabricated.
 *
 * Counter Test: at 6:45 AM a week-one front-desk worker opens a dog and sees —
 * in one glance — vaccine status, allergies, the banned flag, meds, and the
 * emergency vet, before scrolling.
 *
 * Five shell states: loading skeleton (never blank) · error + Retry
 * (cold-start-safe via `request`'s backoff) · not-found 404 (a dog outside the
 * tenant answers 404 — never a leak) · success. Each data section renders its
 * own honest empty state.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  fetchDogProfile,
  fetchDogVaccinationDocument,
  fetchDogVaxRecords,
  humanizeApiError,
  readSession,
  type DogProfile,
  type DogProfileVaccination,
  type DogVaxRecord,
} from '../lib/api.js';
import { DogProfileHeader } from '../components/dog-profile/DogProfileHeader.js';
import { ProfileSection } from '../components/dog-profile/ProfileSection.js';
import { FactList, factsPresent, type Fact } from '../components/dog-profile/FactList.js';
import { VaccinationTable } from '../components/dog-profile/VaccinationTable.js';
import {
  PendingVaxRecordCallouts,
  VaxRecordFiles,
  type OpenDocument,
} from '../components/dog-profile/DogVaxRecords.js';
import { VaxDocumentViewer } from '../components/dog-profile/VaxDocumentViewer.js';
// Ted #32a (E1.6b, #1156) — the desk manages a vaccination record here.
import { VaccinationRecordDialog } from '../components/dog-profile/VaccinationRecordDialog.js';
import { AddBehaviorNoteForm } from '../components/dog-profile/AddBehaviorNoteForm.js';
// Sawyer 12 incident-wording-pet-notes (F3)
import { useTenantVocabulary } from '../lib/useTenantVocabulary.js';
import type { IncidentNouns } from '../lib/tenant-vocabulary.js';
import { EditCareInstructionsForm } from '../components/dog-profile/EditCareInstructionsForm.js';
// SPEC 2026-10-26 AC 6.2 — the desk edits the regular and the second vet.
import { EditVetContactsForm, SECOND_VET_LABEL } from '../components/dog-profile/EditVetContactsForm.js';
// #1116 — the Do Not Admit decision and the desk's spay/neuter answer.
import { DoNotAdmitSwitch } from '../components/dog-profile/DoNotAdmitSwitch.js';
import { SpayNeuterAnswerForm } from '../components/dog-profile/SpayNeuterAnswerForm.js';
// Sawyer 09 pet-inactivity (D1)
import { PetInactivityLine } from '../components/dog-profile/PetInactivityLine.js';
import { CareLogPanel } from '../components/care-log/index.js';
// Sawyer 08 meds-feeding (G3, A1) — staff put meds, times and feeding in here.
import { MedsFeedingProfileAction } from '../components/meds-feeding/MedsFeedingProfileAction.js';
import { medSummary, medTimesText } from '../components/meds-feeding/med-line.js';
import { formatMeals } from '../lib/care-labels.js';
// #1157 E2.2 — the owner's package balance on the dog's page.
import { OwnerPackagesSection } from '../components/dog-profile/OwnerPackagesSection.js';
// Sawyer 11 client-notes-agreements (F1) — the owner's pinned / new client notes.
import { OwnerClientNotesSection } from '../components/dog-profile/OwnerClientNotesSection.js';
import { effectiveRolesOf, rolesHavePermission } from '../lib/rbac-matrix.js';
import { ROUTE_ACCESS_RULES, rolesCanAccess } from '../lib/route-permissions.js';
import {
  deriveAge,
  formatDate,
  formatWholeDollars,
  humanizeAltered,
  humanizeCategory,
  humanizeEnum,
  humanizeFeedingMethod,
  humanizeFoodType,
  humanizeFrequency,
  humanizeSex,
  severityLabel,
  severityLevel,
  vaccineLabel,
} from '../components/dog-profile/format.js';

type DogDetailProps = {
  config: ClientConfig;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; retryable: boolean }
  | { kind: 'not-found' }
  | { kind: 'ready'; dog: DogProfile };

export function DogDetail({ config }: DogDetailProps): JSX.Element {
  const { dogId } = useParams<{ dogId: string }>();
  const navigate = useNavigate();
  const apiUrl = config.deployment.apiUrl;
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [noteFormOpen, setNoteFormOpen] = useState(false);
  const [careFormOpen, setCareFormOpen] = useState(false);
  // #1116 — the desk's spay/neuter answer form (the "Spay/neuter?" pill's
  // only desk-side exit). Refetch on save, like care instructions.
  const [spayNeuterFormOpen, setSpayNeuterFormOpen] = useState(false);

  // Only roles holding pets:write:all may edit care instructions — today
  // owner/manager/receptionist. Groomer, kennel_tech and walker are
  // pets:read:self, so they must not see an affordance the server would
  // 403: showing a button that fails is worse than not showing it.
  // The server check is the authority; this only keeps the UI honest.
  const canEditCare = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'pets:write:all',
  );

  // #721 — logging a meal/med outcome is the #649 append-only-observation
  // class, so it rides `pets.notes:write:all` (walker/groomer/kennel_tech
  // hold it) rather than the narrower `pets:write:all` above. Same rule as
  // canEditCare: only render an affordance the server will accept.
  const canLogCare = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'pets.notes:write:all',
  );

  // Ted #32a (E1.6, #1156) — correcting, verifying, deleting a vaccination
  // or attaching its certificate is `vaccinations:write:all`: the desk trio
  // (owner/manager/receptionist). Same rule as canEditCare: only render an
  // affordance the server will accept.
  const canEditVax = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'vaccinations:write:all',
  );

  const load = useCallback(async (): Promise<void> => {
    if (!dogId) {
      setState({ kind: 'not-found' });
      return;
    }
    setState({ kind: 'loading' });
    const res = await fetchDogProfile(apiUrl, dogId);
    if (res.data) {
      setState({ kind: 'ready', dog: res.data.dog });
      return;
    }
    // A dog outside the tenant (or a bad id) answers 404 → the not-found state.
    const raw = res.error ?? '';
    if (/not found/i.test(raw) || /404/.test(raw)) {
      setState({ kind: 'not-found' });
      return;
    }
    setState({
      kind: 'error',
      message: humanizeApiError(res.error),
      retryable: res.isRetryable ?? true,
    });
  }, [apiUrl, dogId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Ted #32a (#1156) — a quiet re-read after a vaccination write: no
  // skeleton, so the desk keeps its place in the record dialog. A failed
  // re-read leaves the page as it was (the write itself already succeeded).
  const refresh = useCallback(async (): Promise<void> => {
    if (!dogId) return;
    const res = await fetchDogProfile(apiUrl, dogId);
    if (res.data) setState({ kind: 'ready', dog: res.data.dog });
  }, [apiUrl, dogId]);

  const onNoteCreated = useCallback((): void => {
    setNoteFormOpen(false);
    void load();
  }, [load]);

  // Refetch rather than patch local state: the saved value should render from
  // the server, so a save that didn't persist can't look like it did (#614).
  const onCareSaved = useCallback((): void => {
    setCareFormOpen(false);
    void load();
  }, [load]);

  // #1116 — both desk writes re-read the profile so the header pills (the
  // "Spay/neuter?" note, the "Do not admit" chip) come from the server's
  // derivation, never from local optimism.
  const onSpayNeuterSaved = useCallback((): void => {
    setSpayNeuterFormOpen(false);
    void load();
  }, [load]);
  const onDoNotAdmitSaved = useCallback((): void => {
    void load();
  }, [load]);

  // #550 — a staff photo upload persisted server-side; reflect it immediately
  // (and it survives a reload, since the server saved photoUrl).
  const onPhotoUpdated = useCallback((photoUrl: string): void => {
    setState((prev) =>
      prev.kind === 'ready'
        ? { kind: 'ready', dog: { ...prev.dog, photoUrl } }
        : prev,
    );
  }, []);

  return (
    <section
      className="ferro-admin-page ferro-dogprofile"
      aria-busy={state.kind === 'loading'}
    >
      <button
        type="button"
        className="ferro-back"
        onClick={() => navigate(-1)}
        data-testid="dogprofile-back"
      >
        ← Back
      </button>

      {state.kind === 'loading' && (
        <div
          className="ferro-dogprofile__skeleton"
          role="status"
          data-testid="dogprofile-loading"
        >
          <div className="ferro-dogprofile__skeleton-header">
            <div className="ferro-dogprofile__skeleton-photo" />
            <div className="ferro-dogprofile__skeleton-lines">
              <div className="ferro-dogprofile__skeleton-line ferro-dogprofile__skeleton-line--lg" />
              <div className="ferro-dogprofile__skeleton-line ferro-dogprofile__skeleton-line--sm" />
            </div>
          </div>
          <div className="ferro-dogprofile__skeleton-card" />
          <div className="ferro-dogprofile__skeleton-card" />
          <span className="sr-only">Loading pet record…</span>
        </div>
      )}

      {state.kind === 'error' && (
        <div
          className="ferro-dashboard__error"
          role="alert"
          data-testid="dogprofile-error"
        >
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      )}

      {state.kind === 'not-found' && (
        <div
          className="ferro-dogprofile__notfound"
          data-testid="dogprofile-notfound"
        >
          <h1 className="ferro-section__title">Pet not found</h1>
          <p className="ferro-muted">
            This pet record doesn&rsquo;t exist, or it belongs to another
            facility.
          </p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => navigate('/customers')}
          >
            ← Back to customers
          </button>
        </div>
      )}

      {state.kind === 'ready' && (
        <DogProfileBody
          dog={state.dog}
          apiUrl={apiUrl}
          noteFormOpen={noteFormOpen}
          openNoteForm={() => setNoteFormOpen(true)}
          closeNoteForm={() => setNoteFormOpen(false)}
          onNoteCreated={onNoteCreated}
          canEditCare={canEditCare}
          canLogCare={canLogCare}
          careFormOpen={careFormOpen}
          openCareForm={() => setCareFormOpen(true)}
          closeCareForm={() => setCareFormOpen(false)}
          onCareSaved={onCareSaved}
          onPhotoUpdated={onPhotoUpdated}
          spayNeuterFormOpen={spayNeuterFormOpen}
          openSpayNeuterForm={() => setSpayNeuterFormOpen(true)}
          closeSpayNeuterForm={() => setSpayNeuterFormOpen(false)}
          onSpayNeuterSaved={onSpayNeuterSaved}
          onDoNotAdmitSaved={onDoNotAdmitSaved}
          canEditVax={canEditVax}
          onVaxChanged={refresh}
        />
      )}
    </section>
  );
}

interface DogProfileBodyProps {
  dog: DogProfile;
  apiUrl: string;
  noteFormOpen: boolean;
  openNoteForm: () => void;
  closeNoteForm: () => void;
  onNoteCreated: () => void;
  /** pets:write:all — false hides the edit affordance (server still gates). */
  canEditCare: boolean;
  /** #721 pets.notes:write:all — false makes the care log read-only. */
  canLogCare: boolean;
  careFormOpen: boolean;
  openCareForm: () => void;
  closeCareForm: () => void;
  onCareSaved: () => void;
  onPhotoUpdated: (photoUrl: string) => void;
  /** #1116 — desk spay/neuter answer + Do Not Admit. */
  spayNeuterFormOpen: boolean;
  openSpayNeuterForm: () => void;
  closeSpayNeuterForm: () => void;
  onSpayNeuterSaved: () => void;
  onDoNotAdmitSaved: () => void;
  /** Ted #32a (E1.6) — vaccinations:write:all. */
  canEditVax: boolean;
  /** Quiet profile re-read after a vaccination write. */
  onVaxChanged: () => Promise<void>;
}

/** Ted #32a — which vaccination dialog is open, if any. */
type VaxDialogState = { kind: 'add' } | { kind: 'manage'; id: string } | null;

/** The vaccines the name field suggests: the usual three, then the dog's own. */
const COMMON_VACCINES = ['rabies', 'bordetella', 'dhpp'] as const;

function DogProfileBody({
  dog,
  apiUrl,
  noteFormOpen,
  openNoteForm,
  closeNoteForm,
  onNoteCreated,
  canEditCare,
  canLogCare,
  careFormOpen,
  openCareForm,
  closeCareForm,
  onCareSaved,
  onPhotoUpdated,
  spayNeuterFormOpen,
  openSpayNeuterForm,
  closeSpayNeuterForm,
  onSpayNeuterSaved,
  onDoNotAdmitSaved,
  canEditVax,
  onVaxChanged,
}: DogProfileBodyProps): JSX.Element {
  // #752 — the dog's uploaded vaccination records (and their originals).
  // Same grant the server gates the read on: `pets:read:all`, the desk.
  // Floor roles skip the request rather than collect a 403.
  const canSeeVaxRecords = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'pets:read:all',
  );
  // The "Review" link shows exactly when `/vax-inbox`'s RouteGuard would let
  // this viewer in — same function, same rule, so the two cannot disagree.
  const canReviewVax = rolesCanAccess(
    effectiveRolesOf(readSession().staff),
    ROUTE_ACCESS_RULES['/vax-inbox'],
  );
  // #1157 E2.2 — the owner's packages, for the roles the server lets read
  // them (`payments-ops:read:all`, the desk). Floor roles skip the request.
  const canSeePackages = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'payments-ops:read:all',
  );
  // Sawyer 12 incident-wording-pet-notes (F3) — the note POST's own gate
  // (pets.notes:write:all); no "Add pet note" without it. The owner's word
  // for the incident log labels History.
  const canAddNote = rolesHavePermission(
    effectiveRolesOf(readSession().staff),
    'pets.notes:write:all',
  );
  const { incident: incidentNoun } = useTenantVocabulary(apiUrl);
  const [vaxRecords, setVaxRecords] = useState<DogVaxRecord[]>([]);
  const [openDoc, setOpenDoc] = useState<OpenDocument | null>(null);
  // Ted #32a (E1.6) — the record dialog, and a staff-attached certificate
  // open in the viewer. The dialog holds an ID, not a row, so it always
  // renders the row from the latest profile read.
  const [vaxDialog, setVaxDialog] = useState<VaxDialogState>(null);
  const [openCertificate, setOpenCertificate] = useState<DogProfileVaccination | null>(null);
  // SPEC 2026-10-26 AC 6.2 — the vet editor. Offered only to the roles the
  // route accepts (pets:write:all, the same gate as care instructions) and
  // only by a server that sends the second-vet keys, because that server
  // also has the write; a 403 route_not_mapped still hides it (deploy skew).
  const [vetFormOpen, setVetFormOpen] = useState(false);
  const [vetEditUnavailable, setVetEditUnavailable] = useState(false);
  const serverHasSecondVet = dog.secondaryVetName !== undefined;
  const canEditVet = canEditCare && serverHasSecondVet && !vetEditUnavailable;

  const dogId = dog.id;
  useEffect(() => {
    if (!canSeeVaxRecords) return;
    const { token } = readSession();
    if (!token) return;
    let cancelled = false;
    void fetchDogVaxRecords(apiUrl, token, dogId).then((res) => {
      // 'unavailable' (older server, narrower role) and 'error' both leave
      // the panel out: the vaccination table above still stands on its own.
      if (!cancelled) setVaxRecords(res.kind === 'ok' ? res.records : []);
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, dogId, canSeeVaxRecords]);

  // vaccination id → the original it was approved from.
  const vaxSources = useMemo(() => {
    const out = new Map<string, OpenDocument>();
    for (const r of vaxRecords) {
      const first = r.documents[0];
      if (r.status !== 'approved' || !first) continue;
      for (const vid of r.createdVaccinationIds) {
        out.set(vid, { submissionId: r.id, document: first });
      }
    }
    return out;
  }, [vaxRecords]);

  const managedRecord =
    vaxDialog?.kind === 'manage' ? dog.vaccinations.find((v) => v.id === vaxDialog.id) ?? null : null;
  const knownVaccineNames = useMemo(
    () => [...new Set([...COMMON_VACCINES, ...dog.vaccinations.map((v) => v.name)])],
    [dog.vaccinations],
  );

  const hasAlerts = dog.isBanned || Boolean(dog.allergies) || Boolean(dog.specialNeeds);

  const vitalsFacts: Fact[] = [
    { label: 'Breed', value: dog.breed },
    { label: 'Color', value: dog.color },
    { label: 'Size', value: dog.sizeCategory ? humanizeEnum(dog.sizeCategory) : null },
    {
      label: 'Weight',
      value: typeof dog.weight === 'number' ? `${dog.weight} lb` : null,
      numeric: true,
    },
    { label: 'Sex', value: humanizeSex(dog.sex) },
    { label: 'Altered', value: humanizeAltered(dog.altered) },
    { label: 'Altered date', value: formatDate(dog.alteredDate), numeric: true },
    { label: 'Date of birth', value: formatDate(dog.birthDate), numeric: true },
    { label: 'Age', value: deriveAge(dog.birthDate) },
    { label: 'Microchip', value: dog.microchipNumber, numeric: true },
  ];

  const careFacts: Fact[] = [
    { label: 'Feeding method', value: humanizeFeedingMethod(dog.feedingMethod) },
    { label: 'Food type', value: humanizeFoodType(dog.foodType) },
  ];

  const vetFacts: Fact[] = [
    { label: 'Veterinarian', value: dog.vetName },
    {
      label: 'Phone',
      value: dog.vetPhone ? (
        <a className="ferro-dogprofile__link" href={`tel:${dog.vetPhone}`}>
          {dog.vetPhone}
        </a>
      ) : null,
    },
    {
      label: 'Email',
      value: dog.vetEmail ? (
        <a className="ferro-dogprofile__link" href={`mailto:${dog.vetEmail}`}>
          {dog.vetEmail}
        </a>
      ) : null,
    },
    { label: 'Address', value: dog.vetAddress },
  ];
  // #552 (SPEC 2026-10-26 Story 6) — the second regular practice.
  const secondVetFacts: Fact[] = [
    { label: 'Veterinarian', value: dog.secondaryVetName ?? null },
    {
      label: 'Phone',
      value: dog.secondaryVetPhone ? (
        <a className="ferro-dogprofile__link" href={`tel:${dog.secondaryVetPhone}`}>
          {dog.secondaryVetPhone}
        </a>
      ) : null,
    },
    {
      label: 'Email',
      value: dog.secondaryVetEmail ? (
        <a className="ferro-dogprofile__link" href={`mailto:${dog.secondaryVetEmail}`}>
          {dog.secondaryVetEmail}
        </a>
      ) : null,
    },
    { label: 'Address', value: dog.secondaryVetAddress ?? null },
  ];
  const emergencyVetFacts: Fact[] = [
    { label: 'Emergency vet', value: dog.emergencyVetName },
    {
      label: 'Emergency vet phone',
      value: dog.emergencyVetPhone ? (
        <a
          className="ferro-dogprofile__link"
          href={`tel:${dog.emergencyVetPhone}`}
        >
          {dog.emergencyVetPhone}
        </a>
      ) : null,
    },
  ];

  const emergencyFacts: Fact[] = [
    { label: 'Authorized agent', value: dog.emergencyAgent },
    { label: 'Relationship', value: dog.emergencyAgentRelationship },
    {
      label: 'Agent phone',
      value: dog.emergencyAgentPhone ? (
        <a
          className="ferro-dogprofile__link"
          href={`tel:${dog.emergencyAgentPhone}`}
        >
          {dog.emergencyAgentPhone}
        </a>
      ) : null,
    },
    {
      label: 'Vet-care authorization',
      value: dog.emergencyVetCostLimit
        ? `Up to ${formatWholeDollars(dog.emergencyVetCostLimit)}`
        : null,
      numeric: true,
    },
  ];

  const behaviorFacts: Fact[] = [
    { label: 'Temperament', value: dog.temperament ? humanizeEnum(dog.temperament) : null },
    { label: 'Good with', value: dog.goodWith },
    { label: 'Social notes', value: dog.socialNotes },
  ];

  const activeMeds = dog.medications.filter((m) => m.isActive);
  const pastMeds = dog.medications.filter((m) => !m.isActive);

  return (
    <div data-testid="dogprofile-body">
      <DogProfileHeader
        dog={dog}
        apiUrl={apiUrl}
        onAddNote={openNoteForm}
        canAddNote={canAddNote}
        onPhotoUpdated={onPhotoUpdated}
      />

      {/* Loud safety band — the front-desk's #1 glance (banned + allergies). */}
      {hasAlerts && (
        <section
          className="ferro-dogprofile__alerts"
          data-testid="dogprofile-alerts"
        >
          {dog.isBanned && (
            <div className="ferro-dogprofile__alert ferro-dogprofile__alert--danger">
              <strong>Do not admit.</strong>{' '}
              {dog.bannedReason ?? 'This dog is flagged as banned.'}
            </div>
          )}
          {dog.allergies && (
            <div className="ferro-dogprofile__alert ferro-dogprofile__alert--danger">
              <strong>Allergies:</strong> {dog.allergies}
            </div>
          )}
          {dog.specialNeeds && (
            <div className="ferro-dogprofile__alert ferro-dogprofile__alert--warning">
              <strong>Special needs:</strong> {dog.specialNeeds}
            </div>
          )}
        </section>
      )}

      {/* Sawyer 09 pet-inactivity (D1) — renders nothing unless the server says inactive. */}
      <PetInactivityLine inactivity={dog.inactivity} />

      {/* #1116 AC5 — the Do Not Admit decision, right under the alerts band:
          the front desk's first glance. Rendered only when the server sends
          the decision (deploy-window guard); an older server's profile keeps
          the header chip and the alert band it already had. */}
      {dog.doNotAdmit ? (
        <ProfileSection title="Admission" testId="dogprofile-admission">
          <DoNotAdmitSwitch
            apiUrl={apiUrl}
            dogId={dog.id}
            status={dog.doNotAdmit}
            canEdit={canEditCare}
            onSaved={onDoNotAdmitSaved}
          />
        </ProfileSection>
      ) : null}

      {/* Sawyer 11 client-notes-agreements (F1) — the owner's pinned notes
          and notes new to me, after the Admission decision (refuter C7).
          Gated inside (customers:read:all); renders nothing when empty. */}
      {dog.owner ? <OwnerClientNotesSection apiUrl={apiUrl} owner={dog.owner} /> : null}

      {/* #1157 E2.2 — does this visit come out of a package? The owner's
          balance, before the desk reaches checkout. */}
      {canSeePackages && dog.owner ? (
        <OwnerPackagesSection apiUrl={apiUrl} owner={dog.owner} />
      ) : null}

      {/* Identity & Vitals */}
      <ProfileSection
        title="Identity & vitals"
        isEmpty={!factsPresent(vitalsFacts) && !spayNeuterFormOpen && !dog.spayNeuterReminder}
        emptyText="No vitals recorded yet."
        action={
          canEditCare && !spayNeuterFormOpen ? (
            <button
              type="button"
              className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
              onClick={openSpayNeuterForm}
              data-testid="dogprofile-edit-spay-neuter"
            >
              {dog.altered ? 'Change spay/neuter answer' : 'Record spay/neuter answer'}
            </button>
          ) : null
        }
        testId="dogprofile-vitals"
      >
        <FactList facts={vitalsFacts} />
        {spayNeuterFormOpen ? (
          <SpayNeuterAnswerForm
            apiUrl={apiUrl}
            dogId={dog.id}
            current={dog.altered}
            currentSex={dog.sex}
            onSaved={onSpayNeuterSaved}
            onCancel={closeSpayNeuterForm}
          />
        ) : null}
        {/* #1116 AC3 — the reminder's state on the dog, in one sentence the
            desk can read: due / scheduled / queued / sent, or the reason
            none is planned ("birth date missing"). Server-derived; absent
            on an older server. */}
        {dog.spayNeuterReminder ? (
          <p
            className="ferro-dogprofile__note-text"
            data-testid="dogprofile-spay-neuter-reminder"
            data-state={dog.spayNeuterReminder.state}
            data-reason={dog.spayNeuterReminder.reason ?? ''}
          >
            <span className="ferro-dogprofile__note-label">Spay/neuter reminder</span>
            {dog.spayNeuterReminder.summary}
          </p>
        ) : null}
      </ProfileSection>

      {/* Care & Feeding */}
      <ProfileSection
        title="Care & feeding"
        isEmpty={
          !factsPresent(careFacts) &&
          !dog.feedingNotes &&
          !dog.careInstructions &&
          !careFormOpen
        }
        emptyText="No feeding instructions on file — ask the owner at drop-off."
        action={
          canEditCare && !careFormOpen ? (
            <button
              type="button"
              className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
              onClick={openCareForm}
              data-testid="dogprofile-edit-care"
            >
              {dog.careInstructions ? 'Edit care' : 'Add care'}
            </button>
          ) : null
        }
        testId="dogprofile-care"
      >
        <FactList facts={careFacts} />
        {dog.feedingNotes && (
          <p className="ferro-dogprofile__note-text">
            <span className="ferro-dogprofile__note-label">Feeding notes</span>
            {dog.feedingNotes}
          </p>
        )}
        {dog.feedingSlots && dog.feedingSlots.length > 0 ? (
          <p className="ferro-dogprofile__note-text" data-testid="dogprofile-meal-times">
            <span className="ferro-dogprofile__note-label">Meal times</span>
            {formatMeals(dog.feedingSlots)}
          </p>
        ) : null}
        {careFormOpen ? (
          <EditCareInstructionsForm
            apiUrl={apiUrl}
            dogId={dog.id}
            value={dog.careInstructions}
            onSaved={onCareSaved}
            onCancel={closeCareForm}
          />
        ) : (
          dog.careInstructions && (
            <p className="ferro-dogprofile__note-text">
              <span className="ferro-dogprofile__note-label">
                Care instructions
              </span>
              {dog.careInstructions}
            </p>
          )
        )}
      </ProfileSection>

      {/* Medications */}
      <ProfileSection
        title="Medications"
        isEmpty={dog.medications.length === 0 && !dog.medicationNotes}
        emptyText="No medications recorded."
        action={
          // Sawyer 08 meds-feeding (G3) — renders nothing for roles that cannot write.
          // The re-read after a save is the same quiet refresh vaccinations use.
          <MedsFeedingProfileAction apiUrl={apiUrl} dog={dog} onChanged={() => void onVaxChanged()} />
        }
        testId="dogprofile-medications"
      >
        {dog.medicationNotes && (
          <p className="ferro-dogprofile__note-text ferro-dogprofile__note-text--emphasis">
            <span className="ferro-dogprofile__note-label">
              Medication notes
            </span>
            {dog.medicationNotes}
          </p>
        )}
        {activeMeds.length > 0 && (
          <ul className="ferro-dogprofile__list" data-testid="dogprofile-meds-active">
            {activeMeds.map((m) => (
              <li key={m.id} className="ferro-dogprofile__list-item">
                <div className="ferro-dogprofile__list-head">
                  <span className="ferro-dogprofile__list-title">{m.name}</span>
                  <span className="ferro-dogprofile__badge ferro-dogprofile__badge--info">
                    Active
                  </span>
                </div>
                {/* Sawyer A1 — "Apoquel · 16 mg · AM, PM", or the frequency
                    labelled "(times not set)" until staff pick the times. */}
                <span className="ferro-dogprofile__list-meta" data-testid="dogprofile-med-line" title={medSummary(m)}>
                  {[m.dosage, medTimesText(m), m.instructions]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        )}
        {pastMeds.length > 0 && (
          <details className="ferro-dogprofile__details">
            <summary className="ferro-dogprofile__summary">
              Past medications ({pastMeds.length})
            </summary>
            <ul className="ferro-dogprofile__list">
              {pastMeds.map((m) => (
                <li
                  key={m.id}
                  className="ferro-dogprofile__list-item ferro-dogprofile__list-item--muted"
                >
                  <span className="ferro-dogprofile__list-title">{m.name}</span>
                  <span className="ferro-dogprofile__list-meta">
                    {[
                      m.dosage,
                      humanizeFrequency(m.frequency),
                      m.endDate ? `ended ${formatDate(m.endDate)}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </ProfileSection>

      {/* Meals & medications — the #721 care log. Sits directly under
          Medications because that is the order the floor reads in: what the
          dog is ON, then what actually happened at the bowl and the pill.
          The panel owns its own five states, so it is not wrapped in a
          ProfileSection (which would add a second, contradictory empty
          state — §1 one glance, one truth). */}
      <CareLogPanel
        apiUrl={apiUrl}
        dogId={dog.id}
        dogName={dog.name}
        canLog={canLogCare}
      />

      {/* Sawyer 12 incident-wording-pet-notes (F3) — Pet notes: the routine
          note's one obvious home, each note saying who reads it. Writers only
          see the actions (pets.notes:write:all, the server's gate). */}
      <ProfileSection
        title="Pet notes"
        action={
          canAddNote && !noteFormOpen ? (
            <button
              type="button"
              className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
              onClick={openNoteForm}
              data-testid="dogprofile-add-note-section"
            >
              Add pet note
            </button>
          ) : null
        }
        testId="dogprofile-pet-notes"
      >
        <p className="ferro-dogprofile__note-audience-help">
          Staff only notes stay with your team. Shared notes appear in the pet parent&apos;s app.
        </p>
        {noteFormOpen && canAddNote && (
          <AddBehaviorNoteForm
            apiUrl={apiUrl}
            dogId={dog.id}
            onCreated={onNoteCreated}
            onCancel={closeNoteForm}
          />
        )}
        {dog.behaviorNotes.length === 0 ? (
          !noteFormOpen ? (
            <p className="ferro-dogprofile__empty" data-testid="dogprofile-pet-notes-empty">
              {canAddNote
                ? 'No pet notes yet. Add the first one; it stays staff only unless you share it.'
                : 'No pet notes yet.'}
            </p>
          ) : null
        ) : (
          <ul
            className="ferro-dogprofile__timeline"
            data-testid="dogprofile-behavior-notes"
          >
            {dog.behaviorNotes.map((n) => (
              <li key={n.id} className="ferro-dogprofile__timeline-item">
                <div className="ferro-dogprofile__timeline-head">
                  <span
                    className={`ferro-dogprofile__sev ferro-dogprofile__sev--${severityLevel(
                      n.severity,
                    )}`}
                  >
                    {severityLabel(n.severity)}
                  </span>
                  {humanizeCategory(n.category) && (
                    <span className="ferro-dogprofile__timeline-cat">
                      {humanizeCategory(n.category)}
                    </span>
                  )}
                  {/* #714 — channel chip. Text is the signal (color only
                      reinforces); absent on a pre-#714 server payload
                      rather than guessed. */}
                  {n.audience && (
                    <span
                      className={`ferro-dogprofile__audience ferro-dogprofile__audience--${
                        n.audience === 'owner_visible' ? 'owner' : 'staff'
                      }`}
                    >
                      {n.audience === 'owner_visible'
                        ? 'Shared with pet parent'
                        : 'Staff only'}
                    </span>
                  )}
                  <span className="ferro-dogprofile__timeline-date">
                    {formatDate(n.createdAt)}
                  </span>
                </div>
                <p className="ferro-dogprofile__timeline-note">{n.note}</p>
                {n.reportedByName && (
                  <span className="ferro-dogprofile__timeline-by">
                    — {n.reportedByName}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </ProfileSection>

      {/* Behavior — the facts only (Sawyer F3 moved the notes to Pet notes). */}
      <ProfileSection
        title="Behavior"
        isEmpty={!factsPresent(behaviorFacts)}
        emptyText="No behavior details on file."
        testId="dogprofile-behavior"
      >
        <FactList facts={behaviorFacts} />
      </ProfileSection>

      {/* Health & Compliance */}
      <ProfileSection
        title="Health & compliance"
        testId="dogprofile-health"
      >
        <PendingVaxRecordCallouts
          dogName={dog.name}
          records={vaxRecords}
          canReview={canReviewVax}
          onView={setOpenDoc}
        />
        {dog.vaccinations.length === 0 ? (
          <p className="ferro-dogprofile__empty">
            No immunization records on file.
            {canEditVax ? ' Add one from the certificate when the owner sends it.' : ''}
          </p>
        ) : (
          <VaccinationTable
            vaccinations={dog.vaccinations}
            sources={vaxSources}
            onViewSource={setOpenDoc}
            onViewCertificate={setOpenCertificate}
            {...(canEditVax ? { onManage: (v: DogProfileVaccination) => setVaxDialog({ kind: 'manage', id: v.id }) } : {})}
          />
        )}
        {canEditVax ? (
          <div className="ferro-dogprofile__vax-actions">
            <button
              type="button"
              className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
              onClick={() => setVaxDialog({ kind: 'add' })}
              data-testid="dogprofile-vax-add"
            >
              Add a vaccination
            </button>
          </div>
        ) : null}
        <VaxRecordFiles records={vaxRecords} onView={setOpenDoc} />
        {openDoc ? (
          <VaxDocumentViewer
            apiUrl={apiUrl}
            submissionId={openDoc.submissionId}
            document={openDoc.document}
            title={`${dog.name} — vaccination record`}
            onClose={() => setOpenDoc(null)}
          />
        ) : null}
        {openCertificate?.document ? (
          <VaxDocumentViewer
            apiUrl={apiUrl}
            document={openCertificate.document}
            load={(token) =>
              fetchDogVaccinationDocument(apiUrl, token, dog.id, openCertificate.id)
            }
            title={`${dog.name} — ${vaccineLabel(openCertificate.name)} certificate`}
            onClose={() => setOpenCertificate(null)}
          />
        ) : null}
        {vaxDialog && (vaxDialog.kind === 'add' || managedRecord) && !openCertificate && !openDoc ? (
          // No `key`: an add that succeeds becomes "manage the new record" in
          // the SAME dialog, keeping its "added" line and the attach step.
          <VaccinationRecordDialog
            apiUrl={apiUrl}
            dogId={dog.id}
            dogName={dog.name}
            mode={
              vaxDialog.kind === 'manage' && managedRecord
                ? { kind: 'manage', record: managedRecord }
                : { kind: 'add' }
            }
            knownNames={knownVaccineNames}
            onChanged={async (focusId) => {
              await onVaxChanged();
              if (focusId) setVaxDialog({ kind: 'manage', id: focusId });
            }}
            onViewCertificate={setOpenCertificate}
            // #1213 — the next vaccine on the same certificate opens in this
            // same dialog (no `key`), which keeps the uploaded file.
            onAddAnother={() => setVaxDialog({ kind: 'add' })}
            {...(managedRecord && vaxSources.get(managedRecord.id)
              ? { onViewOriginal: () => setOpenDoc(vaxSources.get(managedRecord.id)!) }
              : {})}
            onClose={() => setVaxDialog(null)}
          />
        ) : null}
        {(dog.allergies || dog.specialNeeds) && (
          <div className="ferro-dogprofile__health-alerts">
            {dog.allergies && (
              <p className="ferro-dogprofile__alert ferro-dogprofile__alert--danger">
                <strong>Allergies:</strong> {dog.allergies}
              </p>
            )}
            {dog.specialNeeds && (
              <p className="ferro-dogprofile__alert ferro-dogprofile__alert--warning">
                <strong>Special needs:</strong> {dog.specialNeeds}
              </p>
            )}
          </div>
        )}
        {dog.microchipNumber && (
          <p className="ferro-dogprofile__note-text">
            <span className="ferro-dogprofile__note-label">Microchip</span>
            <span className="ferro-dogprofile__fact-value--num">
              {dog.microchipNumber}
            </span>
          </p>
        )}
      </ProfileSection>

      {/* Veterinarian */}
      <ProfileSection
        title="Veterinarian"
        isEmpty={
          !factsPresent(vetFacts) &&
          !factsPresent(secondVetFacts) &&
          !factsPresent(emergencyVetFacts) &&
          !vetFormOpen
        }
        emptyText={
          canEditVet
            ? 'No veterinarian on file. Add one with “Add vet” when the owner tells you.'
            : 'No veterinarian on file.'
        }
        action={
          canEditVet && !vetFormOpen ? (
            <button
              type="button"
              className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
              onClick={() => setVetFormOpen(true)}
              data-testid="dogprofile-edit-vet"
            >
              {factsPresent(vetFacts) || factsPresent(secondVetFacts) ? 'Edit vet' : 'Add vet'}
            </button>
          ) : null
        }
        testId="dogprofile-vet"
      >
        {vetFormOpen ? (
          <EditVetContactsForm
            apiUrl={apiUrl}
            dogId={dog.id}
            value={{
              vetName: dog.vetName,
              vetPhone: dog.vetPhone,
              vetEmail: dog.vetEmail,
              vetAddress: dog.vetAddress,
              secondaryVetName: dog.secondaryVetName ?? null,
              secondaryVetPhone: dog.secondaryVetPhone ?? null,
              secondaryVetEmail: dog.secondaryVetEmail ?? null,
              secondaryVetAddress: dog.secondaryVetAddress ?? null,
            }}
            onSaved={() => {
              setVetFormOpen(false);
              // The same quiet profile re-read a vaccination write uses.
              void onVaxChanged();
            }}
            onCancel={() => setVetFormOpen(false)}
            onUnavailable={() => setVetEditUnavailable(true)}
          />
        ) : (
          <>
            <FactList facts={vetFacts} />
            {factsPresent(secondVetFacts) && (
              <div className="ferro-dogprofile__subblock" data-testid="dogprofile-second-vet">
                <h3 className="ferro-dogprofile__subtitle">{SECOND_VET_LABEL}</h3>
                <FactList facts={secondVetFacts} />
              </div>
            )}
          </>
        )}
        {factsPresent(emergencyVetFacts) && (
          <div className="ferro-dogprofile__subblock">
            <h3 className="ferro-dogprofile__subtitle">Emergency vet</h3>
            <FactList facts={emergencyVetFacts} />
          </div>
        )}
      </ProfileSection>

      {/* Emergency Authorization */}
      <ProfileSection
        title="Emergency authorization"
        isEmpty={!factsPresent(emergencyFacts)}
        emptyText="No emergency authorization on file — collect at intake."
        testId="dogprofile-emergency"
      >
        <FactList facts={emergencyFacts} />
      </ProfileSection>

      {/* History */}
      <ProfileSection title="History" testId="dogprofile-history">
        <HistoryBlock dog={dog} incident={incidentNoun} />
      </ProfileSection>

      {/* Sawyer 12 incident-wording-pet-notes (F3) — `dogs.notes`, titled by
          who can see it (not by who wrote it: for a Gingr-synced facility the
          sync copies Gingr's animal notes here, refuter C4). */}
      <ProfileSection
        title="Notes the pet parent can see"
        isEmpty={!dog.notes}
        emptyText="No notes the pet parent can see."
        testId="dogprofile-notes"
      >
        <p className="ferro-dogprofile__note-audience-help">
          Shown in the pet parent&apos;s app, and they can change it. Facilities that sync from Gingr also get
          Gingr&apos;s animal notes copied here.
        </p>
        <p className="ferro-dogprofile__note-text">{dog.notes}</p>
      </ProfileSection>
    </div>
  );
}

function HistoryBlock({ dog, incident }: { dog: DogProfile; incident: IncidentNouns }): JSX.Element {
  const empty =
    dog.incidents.length === 0 &&
    dog.reportCards.length === 0 &&
    dog.groomingNotes.length === 0;
  if (empty) {
    return (
      <p className="ferro-dogprofile__empty">
        No {incident.manyLower}, report cards, or grooming notes yet. (Booking history is
        coming soon.)
      </p>
    );
  }
  return (
    <div className="ferro-dogprofile__history">
      <div className="ferro-dogprofile__history-group">
        <h3 className="ferro-dogprofile__subtitle">{incident.many}</h3>
        {dog.incidents.length === 0 ? (
          <p className="ferro-dogprofile__empty">No {incident.manyLower} on file.</p>
        ) : (
          <ul className="ferro-dogprofile__list" data-testid="dogprofile-incidents">
            {dog.incidents.map((i) => (
              <li key={i.id} className="ferro-dogprofile__list-item">
                <div className="ferro-dogprofile__list-head">
                  <span className="ferro-dogprofile__list-title">
                    {humanizeEnum(i.kind)}
                  </span>
                  <span
                    className={`ferro-dogprofile__badge ferro-dogprofile__badge--${
                      i.severity === 'critical' || i.severity === 'high'
                        ? 'danger'
                        : i.severity === 'medium'
                          ? 'warning'
                          : 'neutral'
                    }`}
                  >
                    {humanizeEnum(i.severity)}
                  </span>
                  <span className="ferro-dogprofile__list-meta">
                    {humanizeEnum(i.status)} · {formatDate(i.occurredAt)}
                  </span>
                </div>
                <span className="ferro-dogprofile__list-meta">
                  {i.narrative}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="ferro-dogprofile__history-group">
        <h3 className="ferro-dogprofile__subtitle">Report cards</h3>
        {dog.reportCards.length === 0 ? (
          <p className="ferro-dogprofile__empty">No report cards yet.</p>
        ) : (
          <ul className="ferro-dogprofile__list" data-testid="dogprofile-reportcards">
            {dog.reportCards.map((r) => (
              <li key={r.id} className="ferro-dogprofile__list-item">
                <div className="ferro-dogprofile__list-head">
                  <span className="ferro-dogprofile__list-title">
                    {formatDate(r.createdAt)}
                  </span>
                  {r.mood && (
                    <span className="ferro-dogprofile__badge ferro-dogprofile__badge--neutral">
                      {humanizeEnum(r.mood)}
                    </span>
                  )}
                  {typeof r.rating === 'number' && (
                    <span className="ferro-dogprofile__list-meta">
                      {r.rating}/5
                    </span>
                  )}
                  <span className="ferro-dogprofile__list-meta">
                    {r.sentAt ? `Sent ${formatDate(r.sentAt)}` : 'Not sent'}
                  </span>
                </div>
                <span className="ferro-dogprofile__list-meta">{r.notes}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="ferro-dogprofile__history-group">
        <h3 className="ferro-dogprofile__subtitle">Grooming notes</h3>
        {dog.groomingNotes.length === 0 ? (
          <p className="ferro-dogprofile__empty">No grooming notes yet.</p>
        ) : (
          <ul className="ferro-dogprofile__list" data-testid="dogprofile-grooming">
            {dog.groomingNotes.map((g) => (
              <li key={g.id} className="ferro-dogprofile__list-item">
                <div className="ferro-dogprofile__list-head">
                  <span className="ferro-dogprofile__list-title">
                    {formatDate(g.createdAt)}
                  </span>
                  <span className="ferro-dogprofile__list-meta">
                    {g.originalLanguage === 'es' ? 'Spanish' : 'English'}
                  </span>
                </div>
                <span className="ferro-dogprofile__list-meta">
                  {g.originalText}
                </span>
                {g.translatedText && (
                  <span className="ferro-dogprofile__list-meta ferro-dogprofile__list-meta--translated">
                    {g.translatedText}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
